import { BannerConfig } from "@hub/models/ui/banner-config";
import { ContentImage } from "@hub/models/ui/content-image";
import { Tag } from "@hub/ui/tags/tag";
import { ThemeColors } from "@hub/values/theme-colors.enum";
import Image from "next/legacy/image";
import React from "react";
import styles from "./content-banner.module.css";
import { ArtificialAuthor } from "@hub/data/values/artificial-author";
import { ArticleLanguagePicker } from "@hub/ui/content/article-language-picker";
import { LanguageMetadataModel } from "@hub/models/metadata/language-metadata.model";

const ContentBannerIcon: React.FC<ContentImage> = ({ src, alt }) => {
  return (
    <div className={styles.tile}>
      <div className={styles.icon}>
        <Image src={src} alt={alt} layout="fill" />
      </div>
    </div>
  );
};

interface ContentBannerProps extends BannerConfig {
  languageMetadata?: LanguageMetadataModel | null;
}

export const ContentBanner: React.FC<ContentBannerProps> = ({
  title,
  icons,
  themeColor = ThemeColors.INDIGO,
  updateDate,
  date,
  withNoTopPadding = false,
  tags,
  withTags = true,
  withAuthor = false,
  withDate = true,
  author,
  withBannerDecor = true,
  useSimpleBanner = false,
  languageMetadata,
}) => {
  const match = title.match(/([:])/);

  const delimiter = match ? match[0] : "";

  const titleSections = title.split(/[:]/);

  const preTitle = titleSections[0];

  if (!preTitle) {
    return null;
  }

  const preTitleParts = preTitle.split("+").map((part) => part.trim());
  const preTitleCore = preTitleParts[0];
  const preTitleVariant = preTitleParts[1];

  const postTitle = titleSections[1];

  if (useSimpleBanner) {
    return (
      <div
        style={{ paddingBottom: 0, borderBottom: "none" }}
        className={`${styles.banner}`}
      >
        {languageMetadata && (
          <div className={styles.infoBar}>
            <div className={styles.languagePicker}>
              <ArticleLanguagePicker
                name="language"
                label={"Select Language"}
                languageMetadata={languageMetadata}
              />
            </div>
          </div>
        )}
        <h1 className={styles.title}>
          {!preTitleVariant && (
            <span className={styles.preTitle}>
              {preTitleCore}
              {postTitle ? delimiter : ""}
            </span>
          )}
          {preTitleVariant && (
            <>
              <span className={styles.preTitle}>
                {preTitleCore}
                {` +`}
              </span>
              <span className={styles.preTitle}>
                {preTitleVariant}
                {postTitle ? delimiter : ""}
              </span>
            </>
          )}
          {postTitle && (
            <span className={`${styles.postTitle} ${styles[themeColor]}`}>
              {postTitle}
            </span>
          )}
        </h1>
      </div>
    );
  }

  return (
    <div
      style={withNoTopPadding ? { paddingTop: 0 } : {}}
      className={`${styles.banner}`}
    >
      {(languageMetadata || withBannerDecor) && (
        <div className={styles.infoBar}>
          {withBannerDecor && (
            <div className={styles.icons}>
              {icons &&
                icons.map((icon, index) => (
                  <ContentBannerIcon
                    key={`${icon.src}-${index}`}
                    src={icon.src}
                    alt={icon.alt}
                  />
                ))}
            </div>
          )}
          {languageMetadata && (
            <div className={styles.languagePicker}>
              <ArticleLanguagePicker
                name="language"
                label={"Select Language"}
                languageMetadata={languageMetadata}
              />
            </div>
          )}
        </div>
      )}

      <h1 className={styles.title}>
        {!preTitleVariant && (
          <span className={styles.preTitle}>
            {preTitleCore}
            {postTitle ? delimiter : ""}
          </span>
        )}
        {preTitleVariant && (
          <>
            <span className={styles.preTitle}>
              {preTitleCore}
              {` +`}
            </span>
            <span className={styles.preTitle}>
              {preTitleVariant}
              {postTitle ? delimiter : ""}
            </span>
          </>
        )}
        {postTitle && (
          <span className={`${styles.postTitle} ${styles[themeColor]}`}>
            {postTitle}
          </span>
        )}
      </h1>
      {withTags && tags && (
        <div className={styles.tags}>
          <Tag tag={`v${tags.version}`} themeColor={ThemeColors.WHITE} />
          {tags.variant && (
            <Tag tag={tags.variant} themeColor={ThemeColors.WHITE} />
          )}
          {tags.lifecycle && (
            <Tag tag={tags.lifecycle} themeColor={ThemeColors.PINK} />
          )}
        </div>
      )}
      {date && !updateDate && (
        <span className={styles.date}>Published on {date}</span>
      )}
      {date && updateDate && (
        <span className={styles.date}>Updated on {updateDate}</span>
      )}
      {withAuthor && author && author.name !== ArtificialAuthor.name && (
        <div className={styles.authorContainer}>
          <Image
            className={styles.authorAvatar}
            src={author.avatar}
            alt={`Photo of ${author.name}`}
            height={56}
            width={56}
          />
          <div className={styles.authorInfo}>
            <span className={styles.authorInfo__name}>{author.name}</span>
            <span className={styles.authorInfo__title}>{author.title}</span>
          </div>
        </div>
      )}
    </div>
  );
};
